import * as React from 'react';
import { blundersFit } from './fit.ts';
import { Skeleton } from '@chess/api-skeleton';
import type { EndingCase } from '@chess/api-ending-case';
import { EndingCaseChip, EndingCaseChipShape } from '@chess/api-ending-case/react';

/** One short, independent particular of the game: a label over a value. */
export interface Fact {
  label: string;
  value: string;
}

/** What the bar shows about a game, as v1's GameSummary has it. */
export interface GameSummary {
  white: string;
  black: string;
  /** PGN result: "1-0", "0-1", "1/2-1/2" or "*". */
  resultText: string;
  /** "White won by resignation", "Drawn by repetition", ... */
  outcomeText: string;
  facts: Fact[];
}

const resultLabel = (text: string) => (text === '1/2-1/2' ? '½-½' : text);

/** How the reader's ending went (`@chess/api-ending-case`): the case, and what a click on its chip does. */
export interface EndingChip {
  case: EndingCase;
  /** Moves the board to the ending's first position. */
  onOpen(): void;
}

export interface GameBarProps {
  summary: GameSummary;
  /** Your blunders across the whole game, from the scan; shown only above zero. */
  blunders?: number;
  /** The open game's scan is still running: a short "Scanning…" stands in for the count. */
  scanning?: boolean;
  /** The button left of the title: its words ("Back", "Home") and what a click does. */
  back?: { label: string; onBack(): void };
  /**
   * The ending's case, after the result: a chip that jumps to the ending.
   * `'loading'` while the game's facts are read (a placeholder of the chip's
   * size, so the bar does not shift); absent when the game reached no ending
   * or is not scanned.
   */
  ending?: EndingChip | 'loading';
  /**
   * The game's summary (2026-10-19): the Summary button at the right end,
   * which opens and closes the sheet under the bar (the container's). Pressed
   * while the sheet is open, its icon a × then; the label never changes, so
   * the bar does not shift. No button without it.
   */
  sheet?: { open: boolean; onToggle(): void; controls?: string };
  /** The bar's root element, for the container to place the sheet under it. */
  rootRef?: React.Ref<HTMLElement>;
}

// In the dashboard screens' title style (`shared.dashboardHeader`): no frame,
// the players as the title with the result and how it ended muted after
// " · ", the actions on the right, and Summary last, opening the sheet with
// the particulars and the stages (until 2026-10-19 "More", which opened the
// particulars as a muted line under the bar). A back button, when given, sits
// left of the title.
/* The count hides before the title is cut: the bar measures the natural
   widths of its parts on every resize and keeps the count only while the
   whole row fits (fit.ts). The count's width is remembered from the last
   time it showed, so the row can bring it back when the bar grows. */
function useBlundersFit() {
  const barRef = React.useRef<HTMLDivElement>(null);
  const titleRef = React.useRef<HTMLHeadingElement>(null);
  const countRef = React.useRef<HTMLSpanElement>(null);
  const countWidth = React.useRef(0);
  const [fits, setFits] = React.useState(true);
  React.useLayoutEffect(() => {
    const bar = barRef.current;
    if (!bar) return;
    const measure = () => {
      const title = titleRef.current;
      if (!title) return;
      const count = countRef.current;
      if (count && count.offsetWidth > 0) countWidth.current = count.offsetWidth;
      const gap = parseFloat(getComputedStyle(bar).columnGap) || 0;
      const children = Array.from(bar.children) as HTMLElement[];
      // the row's other parts, at their widths, plus the gaps between every part
      let rest = gap * (children.length - 1);
      for (const child of children) {
        if (child.contains(title)) {
          // the heading: everything in it but the title, with its own gaps
          const heading = child;
          const headingGap = parseFloat(getComputedStyle(heading).columnGap) || 0;
          for (const part of Array.from(heading.children) as HTMLElement[]) {
            if (part !== title) rest += part.offsetWidth + headingGap;
          }
        } else if (child.contains(count)) {
          const actions = child;
          const actionsGap = parseFloat(getComputedStyle(actions).columnGap) || 0;
          for (const part of Array.from(actions.children) as HTMLElement[]) {
            if (part !== count) rest += part.offsetWidth + actionsGap;
          }
          if (count) rest += actionsGap;
        } else {
          rest += child.offsetWidth;
        }
      }
      setFits(blundersFit({ bar: bar.clientWidth, title: title.scrollWidth, rest, blunders: countWidth.current }));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(bar);
    return () => observer.disconnect();
  });
  return { barRef, titleRef, countRef, fits };
}

export const GameBar: React.FC<GameBarProps> = ({ summary, blunders, scanning, back, ending, sheet, rootRef }) => {
  const fit = useBlundersFit();
  const players = `${summary.white} — ${summary.black}`;
  const result = resultLabel(summary.resultText);
  return (
    <header className="review-game-bar" ref={rootRef}>
      <div className="game-bar-bar" ref={fit.barRef}>
        {back && (
          <button
            type="button"
            className="btn btn-ghost game-bar-back"
            data-track="review.bar.back"
            aria-label={back.label === 'Home' ? 'Go to Home' : 'Back to the previous screen'}
            title={back.label === 'Home' ? 'Go to Home' : 'Back to the previous screen'}
            onClick={back.onBack}>
            <span aria-hidden="true">‹</span> {back.label}
          </button>
        )}
        <div className="game-bar-heading">
          <h1 className="game-bar-title" ref={fit.titleRef} title={`${players} · ${result} · ${summary.outcomeText}`}>
            {players}
            <span className="game-bar-period">
              {' · '}
              <span className="game-bar-result">{result}</span>
              {summary.outcomeText ? ` · ${summary.outcomeText}` : ''}
            </span>
          </h1>
          {ending === 'loading' ? (
            <Skeleton loading shape={<EndingCaseChipShape />} radius="999px" inline label="Loading the ending">
              {null}
            </Skeleton>
          ) : (
            ending && <EndingCaseChip case={ending.case} onOpen={ending.onOpen} action="Go to the ending." track="review.bar.ending" />
          )}
        </div>
        <div className="game-bar-actions">
          {scanning ? (
            <span className="game-bar-blunders" title="The engine is scanning this game">
              Scanning…
            </span>
          ) : (
            blunders !== undefined &&
            blunders > 0 && (
              <span
                className={`game-bar-blunders game-bar-blunders-some${fit.fits ? '' : ' game-bar-blunders-hidden'}`}
                ref={fit.countRef}
                hidden={!fit.fits}
                title="Your blunders in this game, from the engine's scan">
                {blunders} {blunders === 1 ? 'blunder' : 'blunders'}
              </span>
            )
          )}
          {sheet && (
            <button
              type="button"
              className={`btn btn-ghost game-bar-more${sheet.open ? ' game-bar-more-on' : ''}`}
              aria-expanded={sheet.open}
              aria-controls={sheet.controls}
              data-track="review.bar.summary"
              data-track-value={sheet.open ? 'close' : 'open'}
              title={sheet.open ? 'Close the summary (Esc)' : 'The game’s particulars and each stage, as on Home'}
              onClick={sheet.onToggle}>
              Summary
              <span className="game-bar-more-icon" aria-hidden="true">
                {sheet.open ? '×' : '▾'}
              </span>
            </button>
          )}
        </div>
      </div>
    </header>
  );
};
